import { useTranslation } from 'react-i18next';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ActionIcon, Anchor, Box, Divider, Group, Stack, Tooltip } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { IconHeadset, IconLogout } from '@tabler/icons-react';
import HeaderShell, { drawerItemClass, navLinkClass } from '@/components/header/HeaderShell';
import AccountMenu, { ACCOUNT_LINKS, AccountIdentity } from '@/components/app/AccountMenu';
import NotificationBell from '@/components/app/notifications/NotificationBell';
import { useChat } from '@/chat/useChat';
import { useCloseOnDesktop } from '@/hooks/useCloseOnDesktop';
import { useProfile } from '@/hooks/useProfile';
import { useIsAgent, useIsCarrier } from '@/hooks/useRoles';
import { useAuth } from '@/stores/authContext';
import { ROUTES } from '@/routes/paths';
import { useActiveNavKey } from '@/components/app/activeNav';
import touchTarget from '@/components/common/touchTarget.module.css';

interface NavItem {
  readonly to: string;
  readonly key: string;
  /** Пути раздела: пункт активен на любой из подстраниц. exact — точное совпадение (для «Главной»). */
  readonly match: readonly string[];
  readonly exact?: boolean;
  /** Пункт только для роли AGENT (рыночный дашборд). */
  readonly agentOnly?: boolean;
  /** Пункт скрыт для агента: агент не публикует прайсы/предложения (Купить/Продать ему не нужны). */
  readonly hideForAgent?: boolean;
  /** Пункт только для роли CARRIER (заявки на перевозку). */
  readonly carrierOnly?: boolean;
  /** Пункт скрыт для перевозчика: он не торгует (Купить/Продать/Сделки/Чаты ему не нужны). */
  readonly hideForCarrier?: boolean;
}

// Раздел остаётся активным на всех своих подстраницах. У «Купить» поиск лучшей цены живёт на
// /app/best-price (вне /app/buy) — поэтому он в списке путей. У «Продать» всё под /app/sell.
// «Главная» — две записи под одним лейблом: у перевозчика она ведёт на его кабинет (/app/carrier),
// у остальных — на общий /app; показывается ровно одна (роли AGENT/CARRIER взаимоисключающие).
export const NAV: readonly NavItem[] = [
  { to: ROUTES.APP, key: 'app.nav.home', match: [ROUTES.APP], exact: true, hideForCarrier: true },
  { to: ROUTES.APP_CARRIER, key: 'app.nav.home', match: [ROUTES.APP_CARRIER], exact: true, carrierOnly: true },
  { to: ROUTES.APP_CARRIER_NEW, key: 'app.nav.carrierNew', match: [ROUTES.APP_CARRIER_NEW], carrierOnly: true },
  { to: ROUTES.APP_CARRIER_IN_WORK, key: 'app.nav.carrierInWork', match: [ROUTES.APP_CARRIER_IN_WORK], carrierOnly: true },
  { to: ROUTES.APP_CARRIER_DONE, key: 'app.nav.carrierDone', match: [ROUTES.APP_CARRIER_DONE], carrierOnly: true },
  { to: ROUTES.APP_AGENT_DASHBOARD, key: 'app.nav.market', match: [ROUTES.APP_AGENT_DASHBOARD], agentOnly: true },
  { to: ROUTES.APP_BUY, key: 'app.nav.buy', match: [ROUTES.APP_BUY, ROUTES.APP_BEST_PRICE], hideForAgent: true, hideForCarrier: true },
  { to: ROUTES.APP_SELL, key: 'app.nav.sell', match: [ROUTES.APP_SELL], hideForAgent: true, hideForCarrier: true },
  { to: ROUTES.APP_DEALS, key: 'app.nav.deals', match: [ROUTES.APP_DEALS], hideForCarrier: true },
  { to: ROUTES.APP_CHAT, key: 'app.nav.chat', match: [ROUTES.APP_CHAT], hideForCarrier: true },
];

/** Активен ли пункт навигации для текущего пути. exact — точное совпадение; иначе — раздел и его подстраницы. */
export function isNavActive(pathname: string, item: Pick<NavItem, 'match' | 'exact'>): boolean {
  if (item.exact) {
    return item.match.includes(pathname);
  }
  return item.match.some((base) => pathname === base || pathname.startsWith(`${base}/`));
}

/** Показывать ли пункт навигации при текущих ролях. Роли AGENT/CARRIER взаимоисключающие. */
export function isNavVisible(
  item: Pick<NavItem, 'agentOnly' | 'hideForAgent' | 'carrierOnly' | 'hideForCarrier'>,
  roles: { readonly isAgent: boolean; readonly isCarrier: boolean },
): boolean {
  const { isAgent, isCarrier } = roles;
  return (
    (!item.agentOnly || isAgent) &&
    !(item.hideForAgent && isAgent) &&
    (!item.carrierOnly || isCarrier) &&
    !(item.hideForCarrier && isCarrier)
  );
}

// Шапка кабинета: лого→/app, разделы (выбранный отмечен), поддержка, уведомления, меню аккаунта.
export default function AppHeader() {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { logout } = useAuth();
  const isAgent = useIsAgent();
  const isCarrier = useIsCarrier();
  const { data: profile } = useProfile();
  const { unreadTotal } = useChat();
  const [opened, { toggle, close }] = useDisclosure(false);
  // Страница вне путей разделов (карточка заявки перевозчика) сама называет свой пункт меню.
  const activeNavKey = useActiveNavKey();
  useCloseOnDesktop(close);

  function handleLogout(): void {
    close();
    logout();
    navigate(ROUTES.LOGIN, { replace: true });
  }

  // Один список разделов для шапки и мобильного меню; различается только оформление пункта.
  const renderNav = (variant: 'desktop' | 'mobile') =>
    NAV.filter((item) => isNavVisible(item, { isAgent, isCarrier })).map((item) => {
      const active = activeNavKey ? item.key === activeNavKey : isNavActive(location.pathname, item);
      return (
        <Anchor
          key={item.to}
          component={Link}
          to={item.to}
          onClick={close}
          underline="never"
          className={variant === 'desktop' ? navLinkClass(active) : drawerItemClass(active)}
          aria-current={active ? 'page' : undefined}
        >
          <Group gap={6} wrap="nowrap" component="span">
            {t(item.key)}
            {item.to === ROUTES.APP_CHAT && unreadTotal > 0 ? (
              <Box
                component="span"
                role="img"
                w={8}
                h={8}
                style={{ borderRadius: '50%', background: 'var(--mantine-color-brand-7)', display: 'inline-block', flexShrink: 0 }}
                aria-label={t('app.chat.unreadShort')}
              />
            ) : null}
          </Group>
        </Anchor>
      );
    });

  const actions = (
    <>
      {/* На телефоне поддержка подписана в меню: значок без подписи там непонятен, а места в шапке нет. */}
      <Box visibleFrom="xs">
        <Tooltip label={t('app.support')}>
          <ActionIcon
            component="a"
            href={ROUTES.SUPPORT_URL}
            target="_blank"
            rel="noopener noreferrer"
            variant="subtle"
            color="gray"
            className={touchTarget.target}
            aria-label={t('app.support')}
          >
            <IconHeadset size={20} />
          </ActionIcon>
        </Tooltip>
      </Box>
      <NotificationBell />
      <Box visibleFrom="md">
        <AccountMenu />
      </Box>
    </>
  );

  const menu = (
    <>
      <Stack component="nav" gap={4} aria-label={t('nav.mainLabel')}>
        {renderNav('mobile')}
      </Stack>

      <Divider my="xs" />

      <Box px="sm" pb="xs">
        <AccountIdentity profile={profile} />
      </Box>
      {ACCOUNT_LINKS.map(({ to, key, Icon }) => (
        <Anchor key={to} component={Link} to={to} onClick={close} underline="never" className={drawerItemClass()}>
          <Icon size={18} />
          {t(key)}
        </Anchor>
      ))}
      <Anchor
        href={ROUTES.SUPPORT_URL}
        target="_blank"
        rel="noopener noreferrer"
        onClick={close}
        underline="never"
        className={drawerItemClass()}
      >
        <IconHeadset size={18} />
        {t('app.support')}
      </Anchor>
      <Anchor
        component="button"
        type="button"
        onClick={handleLogout}
        underline="never"
        className={drawerItemClass()}
        style={{ color: 'var(--mantine-color-red-9)' }}
      >
        <IconLogout size={18} />
        {t('nav.logout')}
      </Anchor>
    </>
  );

  return (
    <HeaderShell
      logoTo={isCarrier ? ROUTES.APP_CARRIER : ROUTES.APP}
      nav={renderNav('desktop')}
      actions={actions}
      menuOpened={opened}
      onMenuToggle={toggle}
      onMenuClose={close}
      menu={menu}
    />
  );
}
